// VoiceAllocator — 6-voice polyphony with a switchable mono mode (D1).
//
// Poly: each note grabs a free voice, or steals the oldest. Mono: all notes
// collapse onto one voice with last-note priority — a held-note stack means
// releasing the top note glides (legato) back to the previous held note.
//
// Parameter changes broadcast to every voice via `each`; modulation targets are
// gathered across all voices so one global LFO drives the whole pool.

import { Voice, type OscIndex } from './Voice';
import type { ParamId } from './params';

export const POLY_VOICES = 6;
export type VoiceMode = 'poly' | 'mono';

export class VoiceAllocator {
  private readonly voices: Voice[];
  private mode: VoiceMode = 'poly';
  private glide = 0;

  // Poly bookkeeping.
  private noteOf: (number | null)[];
  private orderOf: number[];
  private order = 0;

  // Mono bookkeeping: notes currently held, oldest first.
  private heldStack: number[] = [];

  constructor(voices: Voice[]) {
    this.voices = voices;
    this.noteOf = voices.map(() => null);
    this.orderOf = voices.map(() => 0);
  }

  setMode(mode: VoiceMode): void {
    if (mode === this.mode) return;
    this.allNotesOff(); // avoid stuck notes across the switch
    this.mode = mode;
  }

  setGlide(seconds: number): void {
    this.glide = Math.max(0, seconds);
  }

  noteOn(midi: number, when?: number): void {
    if (this.mode === 'mono') this.monoNoteOn(midi, when);
    else this.polyNoteOn(midi, when);
  }

  noteOff(midi: number, when?: number): void {
    if (this.mode === 'mono') this.monoNoteOff(midi, when);
    else this.polyNoteOff(midi, when);
  }

  /** Run `fn` on every voice (used to broadcast parameter changes). */
  each(fn: (v: Voice) => void): void {
    for (const v of this.voices) fn(v);
  }

  /** All live AudioParams backing `id`, across the pool (for LFO routing). */
  getModTargets(id: ParamId): AudioParam[] {
    const out: AudioParam[] = [];
    for (const v of this.voices) {
      const t = v.getModTarget(id);
      if (t) out.push(t);
    }
    return out;
  }

  panic(when?: number): void {
    for (const v of this.voices) v.kill(when);
    this.reset();
  }

  // --- Poly ---

  private polyNoteOn(midi: number, when?: number): void {
    let idx = this.noteOf.indexOf(midi); // retrigger same note
    if (idx < 0) idx = this.noteOf.indexOf(null); // free voice
    if (idx < 0) idx = this.oldestVoice(); // steal
    this.voices[idx]!.noteOn(midi, when);
    this.noteOf[idx] = midi;
    this.orderOf[idx] = ++this.order;
  }

  private polyNoteOff(midi: number, when?: number): void {
    for (let i = 0; i < this.voices.length; i++) {
      if (this.noteOf[i] === midi) {
        this.voices[i]!.noteOff(when);
        this.noteOf[i] = null;
      }
    }
  }

  private oldestVoice(): number {
    let idx = 0;
    let min = Infinity;
    for (let i = 0; i < this.orderOf.length; i++) {
      if (this.orderOf[i]! < min) {
        min = this.orderOf[i]!;
        idx = i;
      }
    }
    return idx;
  }

  // --- Mono (last-note priority) ---

  private monoNoteOn(midi: number, when?: number): void {
    this.removeHeld(midi);
    this.heldStack.push(midi);
    this.voices[0]!.noteOn(midi, when, this.glide);
  }

  private monoNoteOff(midi: number, when?: number): void {
    this.removeHeld(midi);
    const top = this.heldStack[this.heldStack.length - 1];
    if (top === undefined) {
      this.voices[0]!.noteOff(when);
    } else {
      this.voices[0]!.glideTo(top, when, this.glide); // legato fallback
    }
  }

  private removeHeld(midi: number): void {
    const i = this.heldStack.indexOf(midi);
    if (i >= 0) this.heldStack.splice(i, 1);
  }

  private allNotesOff(): void {
    for (const v of this.voices) v.noteOff();
    this.reset();
  }

  private reset(): void {
    this.noteOf = this.voices.map(() => null);
    this.orderOf = this.voices.map(() => 0);
    this.order = 0;
    this.heldStack = [];
  }
}

export type { OscIndex };
